import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { PaymentBadge } from "@/components/PaymentStatus";
import type { DurationBreakdown, DurationIssue, PaymentInfo } from "@/lib/api";
import { durationQualityNote } from "@/lib/sessionDurations";
import { formatDuration, formatEnergy } from "@voltie/localization-web";
import { useT, type Translate } from "@/app/localization";
import { energyFromWh, moneyHundredths, moneyMajor, viewerInstant } from "@/app/displayFormat";

/** One finalized billing line item (backend-computed; the UI only formats). */
interface LineItem {
  start_at?: string | null;
  end_at?: string | null;
  fee_type: string;
  uom: string;
  rate: number;            // major units of the currency per uom
  amount_cents: number;    // fixed hundredths of the major unit in every currency (billing_engine._line)
  pricing_reason?: string;
  billable?: boolean;
  energy_kwh?: number;
  hours?: number;
  approximation?: string;
}

export interface HistorySession {
  session_id: string;
  start_time?: string | null;
  end_time?: string | null;
  duration_seconds?: number;
  // Charging time only (idle/suspended excluded). Always a number in the published
  // contract; whether it can be trusted is duration_breakdown.
  charging_seconds?: number;
  // Additive split fields (null = never recorded). Absent on payloads from a backend
  // that predates the split — those keep the original two timing fields only.
  idle_seconds?: number | null;
  suspended_seconds?: number | null;
  duration_breakdown?: DurationBreakdown;
  duration_issues?: DurationIssue[];
  energy_delivered?: number; // Wh
  customer_name?: string | null;
  customer_email?: string | null;
  authorized_by?: string;
  ended_by?: string | null;
  pricing_profile_name?: string | null;
  pricing_source?: string | null;
  billing_source?: string;
  // The session row's billing_status (pending | finalizing | failed | finalized |
  // not_authorized | null for legacy).
  billing_status?: string | null;
  costs?: {
    total_cost?: number | null;
    currency_code?: string;
    line_items?: LineItem[];
  };
  // Payment status + collected-vs-billed, projected the SAME way as the admin org
  // sessions report (attached by GET /api/unit/<id>/sessions). Absent on legacy /
  // dev-memory sessions — the dialog simply omits the Payment section then.
  payment?: PaymentInfo;
  // Client-only: set when the payment projection failed to load for this row's
  // page (response payment_available === false) so the dialog can show
  // "Unavailable" instead of silently omitting the amounts.
  paymentUnavailable?: boolean;
}

function durationText(seconds?: number | null): string {
  if (seconds == null || seconds < 0) return "—";
  return formatDuration(seconds, "seconds");
}

export interface DurationField {
  /** Stable row identity; the label is display text only. */
  id: "plugged_in" | "charging" | "idle" | "suspended";
  label: string;
  value: string;
  sub?: string;
}

/** Plugged-in / charging / idle / suspended rows for the dialog. Idle is only ever the
 *  recorded idle — never plugged-in minus charging — and a value the backend could not
 *  establish shows "—", not the number kept for API compatibility. */
export function historyDurationFields(t: Translate, s: HistorySession): DurationField[] {
  const plugged: DurationField = {
    id: "plugged_in",
    label: t("operator.session_detail.plugged_in_time"),
    value: durationText(s.duration_seconds),
  };
  const chargingLabel = t("operator.session_detail.charging_time");
  const breakdown = s.duration_breakdown;
  if (breakdown == null) {
    // Older payload: the fields it always had, no invented breakdown.
    return [plugged, { id: "charging", label: chargingLabel, value: durationText(s.charging_seconds) }];
  }
  const quality = durationQualityNote(breakdown, s.duration_issues, t)?.label;
  const notRecorded = t("operator.session_detail.not_recorded");
  const idleLabel = t("operator.session_detail.idle_time");
  const charging: DurationField = breakdown === "unknown"
    ? { id: "charging", label: chargingLabel, value: "—", sub: notRecorded }
    : { id: "charging", label: chargingLabel, value: durationText(s.charging_seconds), sub: quality };
  const idle: DurationField = s.idle_seconds == null
    ? { id: "idle", label: idleLabel, value: "—", sub: notRecorded }
    : { id: "idle", label: idleLabel, value: durationText(s.idle_seconds), sub: quality };
  const fields: DurationField[] = [plugged, charging, idle];
  if (s.suspended_seconds != null && s.suspended_seconds > 0) {
    fields.push({ id: "suspended", label: t("operator.session_detail.suspended_time"), value: durationText(s.suspended_seconds), sub: quality });
  }
  return fields;
}

/** Line items are keyed by fee type and unit of measure; unknown pairs keep the raw fee type. */
const FEE_KEY: Record<string, string> = {
  "charging|kwh": "billing.line_item.fee_energy",
  "charging|hour": "billing.line_item.fee_charging_time",
  "idle|hour": "billing.line_item.fee_idle",
  "activation|session": "billing.line_item.fee_activation",
};

function feeLabel(t: Translate, li: LineItem): string {
  const key = FEE_KEY[`${li.fee_type}|${li.uom}`];
  return key ? t(key) : li.fee_type;
}

function qtyText(li: LineItem): string {
  if (li.energy_kwh != null) return formatEnergy(li.energy_kwh, "kWh", "meter");
  if (li.hours != null) return durationText(li.hours * 3600);
  return "";
}

const SOURCE_VALUES = new Set(["pricing_profile", "unit_default"]);
const AUTHORIZED_VALUES = new Set(["admin", "customer", "auto"]);

function sourceLabel(t: Translate, source?: string | null): string | null {
  return source && SOURCE_VALUES.has(source) ? t("operator.session_detail.pricing_source", { source }) : null;
}

function authorizedLabel(t: Translate, by?: string): string | null {
  return by && AUTHORIZED_VALUES.has(by) ? t("operator.session_detail.authorized_by", { by }) : null;
}

/**
 * Read-only detail dialog for one completed session from the device-page
 * history. Renders the finalized per-interval billing line items (paid AND
 * free segments) — no tariff math happens here.
 */
export function HistorySessionDetail({ session, showCosts, onClose }: {
  session: HistorySession | null;
  showCosts: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const s = session;
  const items = s?.costs?.line_items ?? [];
  // The billing resolver always names the session's currency; it is never defaulted here.
  const currency = s?.costs?.currency_code;
  const totalCost = s?.costs?.total_cost;
  const notFinalized = s?.billing_source === "pending" || s?.billing_source === "failed";
  const notAuthorized = s?.billing_status === "not_authorized";
  // Finalized, but no invoice could be read: the amount stays unknown (never a $0).
  const invoiceMissing = s?.billing_status === "finalized";
  const durationNote = s ? durationQualityNote(s.duration_breakdown, s.duration_issues, t) : null;

  return (
    <Dialog open={!!s} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg">
        {s && (
          <>
            <DialogHeader>
              <DialogTitle>{t("operator.session.details_title")}</DialogTitle>
              <DialogDescription className="font-mono text-xs">{s.session_id}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Timing + energy overview */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem label={t("operator.session_detail.start")} value={viewerInstant(s.start_time) ?? "—"} />
                <DetailItem label={t("operator.session_detail.end")} value={viewerInstant(s.end_time) ?? "—"} />
                {historyDurationFields(t, s).map((f) => (
                  <DetailItem key={f.id} label={f.label} value={f.value} sub={f.sub} />
                ))}
                <DetailItem label={t("operator.session_detail.energy_delivered")} value={energyFromWh(s.energy_delivered) ?? "—"} />
                {showCosts && (
                  <DetailItem
                    label={t("billing.amount.billed")}
                    value={
                      moneyMajor(totalCost, currency)
                        ?? (notFinalized
                          ? t("billing.state.not_finalized")
                          : notAuthorized
                            ? t("billing.state.not_authorized")
                            : invoiceMissing
                              ? t("billing.state.invoice_missing")
                              : "—")
                    }
                  />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <DetailItem
                    label={t("billing.amount.captured")}
                    value={
                      s.payment
                        // Payment amounts are USD by the projection contract (session_report._usd).
                        ? (moneyMajor(s.payment.captured_payment, "USD") ?? "—")
                        : t("common.state.unavailable")
                    }
                  />
                )}
                {showCosts && (s.payment || s.paymentUnavailable) && (
                  <div>
                    <div className="text-xs opacity-60">{t("operator.session_history.column_payment")}</div>
                    <div className="mt-0.5">
                      {s.payment ? (
                        <PaymentBadge
                          state={s.payment.payment_state}
                          needsAttention={s.payment.payment_needs_attention}
                        />
                      ) : (
                        <span className="text-xs opacity-60">{t("common.state.unavailable")}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {durationNote && (
                <div className="text-xs opacity-70" data-testid="duration-quality-note">
                  {durationNote.detail}
                </div>
              )}

              <Separator />

              {/* Who started it + which tariff applied */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <DetailItem
                  label={t("operator.session_detail.started_by")}
                  value={s.customer_name || authorizedLabel(t, s.authorized_by) || s.authorized_by || "—"}
                  sub={s.customer_email ?? undefined}
                />
                <DetailItem
                  label={t("operator.session_detail.tariff")}
                  value={s.pricing_profile_name
                    || sourceLabel(t, s.pricing_source)
                    || (s.pricing_source ?? "—")}
                  sub={s.pricing_profile_name ? (sourceLabel(t, s.pricing_source) ?? undefined) : undefined}
                />
              </div>

              {/* Per-interval rate breakdown — paid AND free segments */}
              {showCosts && (
                <>
                  <Separator />
                  <div>
                    <div className="text-xs font-semibold uppercase opacity-70 mb-2">{t("operator.session_detail.rate_intervals")}</div>
                    {items.length > 0 ? (
                      <div className="space-y-2" data-testid="session-line-items">
                        {items.map((li, i) => (
                          <div key={i} className="flex items-start justify-between gap-3 text-sm">
                            <div>
                              <div className="flex items-center gap-2">
                                <span>{feeLabel(t, li)}</span>
                                {li.rate === 0 ? (
                                  <Badge variant="success">{t("billing.line_item.free")}</Badge>
                                ) : li.billable === false ? (
                                  <Badge variant="secondary">{t("billing.line_item.not_charged")}</Badge>
                                ) : null}
                              </div>
                              <div className="text-xs opacity-60">
                                {li.start_at && li.end_at
                                  ? t("billing.line_item.interval", {
                                      start: viewerInstant(li.start_at, "time") ?? "",
                                      end: viewerInstant(li.end_at, "time") ?? "",
                                    })
                                  : null}
                                {qtyText(li) ? ` · ${qtyText(li)}` : null}
                                {li.rate > 0
                                  ? ` ${t("billing.line_item.rate", { rate: moneyMajor(li.rate, currency) ?? "", uom: li.uom })}`
                                  : null}
                              </div>
                            </div>
                            <div className="tabular-nums font-medium shrink-0">
                              {moneyHundredths(li.amount_cents, currency)}
                            </div>
                          </div>
                        ))}
                        {totalCost != null && (
                          <div className="flex items-center justify-between border-t border-border pt-2 text-sm font-semibold">
                            <span>{t("billing.line_item.total")}</span>
                            <span className="tabular-nums">{moneyMajor(totalCost, currency)}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm opacity-60">
                        {notFinalized
                          ? t("operator.session_detail.billing_not_finalized")
                          : t("operator.session_detail.no_breakdown")}
                      </div>
                    )}
                  </div>
                </>
              )}

              {s.ended_by && (
                <div className="text-xs opacity-60">{t("operator.session_detail.ended_by", { reason: s.ended_by })}</div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs opacity-60">{label}</div>
      <div className="font-semibold">{value}</div>
      {sub && <div className="text-xs opacity-60">{sub}</div>}
    </div>
  );
}
